import { useState, useRef, useCallback } from 'react'
import { Upload, X, Image as ImageIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatFileSize } from '@/lib/utils'
import { ALLOWED_MEDIA_TYPES, MAX_FILE_SIZE, type AllowedMediaType } from '@/types/media'
import { getPresignedUrl, uploadToR2 } from '@/lib/api'

interface ImageUploaderProps {
  value?: string
  onChange: (url: string) => void
  className?: string
}

export function ImageUploader({ value, onChange, className }: ImageUploaderProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_MEDIA_TYPES.includes(file.type as AllowedMediaType)) {
      return 'Type de fichier non supporte. Utilisez JPG, PNG, WebP ou GIF.'
    }
    if (file.size > MAX_FILE_SIZE) {
      return `Fichier trop volumineux. Maximum ${formatFileSize(MAX_FILE_SIZE)}.`
    }
    return null
  }, [])

  const handleUpload = useCallback(async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      return
    }

    setError(null)
    setIsUploading(true)
    setProgress(0)

    try {
      // Get presigned URL
      const { uploadUrl, publicUrl } = await getPresignedUrl({
        filename: file.name,
        contentType: file.type,
        size: file.size,
      })

      // Upload to R2
      await uploadToR2(uploadUrl, file, setProgress)

      // Update value
      onChange(publicUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'upload')
    } finally {
      setIsUploading(false)
      setProgress(0)
    }
  }, [onChange, validateFile])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleUpload(file)
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [handleUpload])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const file = e.dataTransfer.files[0]
    if (file) {
      handleUpload(file)
    }
  }, [handleUpload])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleRemove = useCallback(() => {
    onChange('')
    setError(null)
  }, [onChange])

  return (
    <div className={cn('space-y-2', className)}>
      {/* Preview */}
      {value && (
        <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden group">
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
            title="Supprimer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Upload zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
          isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400',
          isUploading && 'pointer-events-none'
        )}
      >
        {isUploading ? (
          <div className="space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-sm text-gray-500">Upload en cours... {progress}%</span>
          </div>
        ) : (
          <div className="space-y-2">
            {value ? (
              <ImageIcon className="mx-auto h-8 w-8 text-gray-400" />
            ) : (
              <Upload className="mx-auto h-8 w-8 text-gray-400" />
            )}
            <p className="text-sm text-gray-500">
              {value ? 'Changer l\'image' : 'Cliquez ou deposez une image'}
            </p>
            <p className="text-xs text-gray-400">
              JPG, PNG, WebP, GIF - Max {formatFileSize(MAX_FILE_SIZE)}
            </p>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_MEDIA_TYPES.join(',')}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  )
}
